import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How JavaScript linters cause bugs",
  "description": "",
  "published": "2017-09-14T00:00:00.000Z",
  "redirect_from": ["/blog/javascript-linters-bugs/swizec/7800"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/09/DJoDVIiVYAAOcPs.jpg-large.jpeg",
        "alt": null
      }}></img></p>
    <p>{`I was doing code review for a coworker yesterday, and it soon became obvious that he used a linter and that the linter gave him a bright idea: `}<em parentName="p">{`use strict comparisons`}</em>{`.`}</p>
    <p>{`Using strict comparisons is a great rule to follow. `}<inlineCode parentName="p">{`===`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`==`}</inlineCode>{`, `}<inlineCode parentName="p">{`!==`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`!=`}</inlineCode>{`. Your life will be better.`}</p>
    <p>{`You're ensuring not only that your `}<em parentName="p">{`values`}</em>{` are equal or unequal, you ensure their `}<em parentName="p">{`types`}</em>{` match as well. Because `}<a parentName="p" {...{
        "href": "https://www.destroyallsoftware.com/talks/wat"
      }}>{`JavaScript has funny types`}</a>{`, strict comparison lets you avoid painful pain.`}</p>
    <p>{`Things like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`> "0" == 0
true
`}</code></pre>
    <p>{`And like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`> true == 1
true
> false == 0
true
`}</code></pre>
    <p>{`Even things as silly as this:`}</p>
    <pre><code parentName="pre" {...{}}>{`> new Array() == 0
true
> [] == 0
true
`}</code></pre>
    <p>{`Run a linter on those examples, and it will sagely say `}<em parentName="p">{`"Dude, use strict comparison. ALWAYS use strict comparison.”`}</em></p>
    <p>{`And your linter would be right. `}<inlineCode parentName="p">{`===`}</inlineCode>{` fixes all of those examples.`}</p>
    <pre><code parentName="pre" {...{}}>{`> "0" === 0
false
> true === 1
false
> false === 0
false
> new Array() === 0
false
> [] === 0
false
`}</code></pre>
    <p>{`👌 Problem solved. 👌`}</p>
    <p>{`But here's one situation where it gets tricky. Checking undefinedness:`}</p>
    <pre><code parentName="pre" {...{}}>{`> null == undefined
true
> null === undefined
false
`}</code></pre>
    <p>{`Under loose comparison, `}<inlineCode parentName="p">{`null`}</inlineCode>{` and `}<inlineCode parentName="p">{`undefined`}</inlineCode>{` are equal. Under strict, they're not.`}</p>
    <p>{`This can cause all sorts of issues.`}</p>
    <p>{`Here are some examples I found in my coworker's PR. 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`const url = $(elem).data('url')
if (url !== null) {
    // ...
}
`}</code></pre>
    <p>{`But if your `}<inlineCode parentName="p">{`elem`}</inlineCode>{` doesn't have a `}<inlineCode parentName="p">{`data-url="..."`}</inlineCode>{` attribute, jQuery returns `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, not `}<inlineCode parentName="p">{`null`}</inlineCode>{`. Strict comparison fails.`}</p>
    <p>{`A better approach is to use `}<inlineCode parentName="p">{`if (url)`}</inlineCode>{` because `}<inlineCode parentName="p">{`undefined`}</inlineCode>{` is falsey and so is an empty string. That makes your code robust against `}<inlineCode parentName="p">{`data-url=""`}</inlineCode>{` :)`}</p>
    <pre><code parentName="pre" {...{}}>{`function scroll({ elem, offset, duration }) {
    duration = duration !== null ? duration : 2000;
}
`}</code></pre>
    <p>{`But if you call `}<inlineCode parentName="p">{`scroll()`}</inlineCode>{` without duration, it's `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, and your code breaks. No default duration for you. A better approach is to use destructuring defaults, like this: `}<inlineCode parentName="p">{`function scroll({ elem, offset, duration = 2000 })`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`function get_id(widget) {
    let id = widget.id;
    if (id !== null) {
        // ...
    }
}
`}</code></pre>
    <p>{`But reading an inexistent object property returns `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, not `}<inlineCode parentName="p">{`null`}</inlineCode>{`, and this code breaks. Once more, you're better of relying on inherent falsiness 👉 `}<inlineCode parentName="p">{`if (id)`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`function createWidget(defaultText, onClick, markBusy) {
    new Widget({
        text: defaultText,
        onClick: onClick,
        markBusy: markBusy !== null && markBusy
    })
}
`}</code></pre>
    <p>{`This one is tricky. It's trying to pass `}<inlineCode parentName="p">{`markBusy`}</inlineCode>{` into the `}<inlineCode parentName="p">{`Widget`}</inlineCode>{` constructor, but only if it's defined. You can't use default param values because there's no destructuring so hmm… 🤔`}</p>
    <p>{`Then again, the whole exercise is futile. You can achieve the same effect if you rely on inherent falseyness: `}<inlineCode parentName="p">{`markBusy: !!markBusy`}</inlineCode>{`.`}</p>
    <p>{`I guess my point is that you have to be careful. Don't blindly trust your linter when it says change `}<inlineCode parentName="p">{`this code`}</inlineCode>{` to `}<inlineCode parentName="p">{`that code`}</inlineCode>{`.`}</p>
    <p>{`Happy hacking 🤓`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      